import { Feature, Geometry, LineString, Point, Polygon, Position } from 'geojson';
import { MapMouseEvent } from '@mapbox/mapbox-gl-draw';
import { AbstractDraw, DrawInteractionState } from './abstract-draw';
import { GeometryUtil } from '../geometry';
import { SnapPoint } from '../snap';
import { FeatureUtil } from '../feature';

export class AbstractDrawCircle extends AbstractDraw<DrawInteractionState> {
  protected line: Feature<LineString> | undefined;
  protected text: Feature<Point> | undefined;
  protected firstPoint: Feature<Point> | undefined;
  protected lastPoint: Feature<Point> | undefined;
  protected polygon: Feature<Polygon> | undefined;

  buildInitialState(initialState: DrawInteractionState): DrawInteractionState {
    let state = super.buildInitialState(initialState);

    this._generateDrawLine(state);

    return state;
  }

  canSave(state: DrawInteractionState): boolean {
    return state.vertexList.length > 1;
  }

  async onMouseMove(state: DrawInteractionState, e: MapMouseEvent) {
    await super.onMouseMove(state, e);

    this.aim.geometry.coordinates = this.snapPoint.point as Position;

    this.displayDrawFeature(this.aim);

    if (this.line) {
      this._generateDrawLine(state, this.snapPoint);
    }
  }

  onStop(state: DrawInteractionState) {
    if (this.firstPoint) {
      this.removeDrawFeature(this.firstPoint.id);
      this.firstPoint = undefined;
    }
    if (this.lastPoint) {
      this.removeDrawFeature(this.lastPoint.id);
      this.lastPoint = undefined;
    }
    if (this.polygon) {
      this.removeDrawFeature(this.polygon.id);
      this.polygon = undefined;
    }
    super.onStop(state);
  }

  async onClick(state: DrawInteractionState, e: MapMouseEvent) {
    await super.onClick(state, e);

    if (e.defaultPrevented) {
      return;
    }

    if(state.vertexList.length === 2){


      state.vertexList = this.polygon.geometry.coordinates[0]
      this.save(state);
    }

    this._generateDrawLine(state);
    this.tick();
  }

  async setCoordinates(state: DrawInteractionState, points: Position[]): Promise<void> {
    this.clearDrawFeatures();
    await super.setCoordinates(state, points);

    this._generateDrawLine(state);
    this.tick();
  }

  save(state: DrawInteractionState, nextInteraction: string | undefined = undefined) {
    const circle = GeometryUtil.circle([...state.vertexList, state.vertexList[0]])

    circle.properties= {meta_type: 'circle'} ;

    this.createLayerGeometries([circle], nextInteraction);
  }

  getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  protected _generateDrawLine(state: DrawInteractionState, lastPoint: SnapPoint | undefined = undefined): void {
    if (state.vertexList.length === 0 && this.line) {
      this.removeDrawFeature(this.line.id.toString());
      this.removeDrawFeature(this.text.id.toString());
      this.line = undefined;
      return;
    } else if (state.vertexList.length === 0) {
      return;
    }
    let vertexList = [...state.vertexList];

    if (lastPoint) {
      if (this.controller.snapUtility.isMode('trace') && state.snapFeature) {
        let trace = FeatureUtil.extractLineFrom(
          state.snapFeature.geometry as any,
          state.snapFeaturePath,
          lastPoint.path
        );
        vertexList.push(...trace);
      }
      vertexList.push(lastPoint.point);
    }
    this.line = this.createLineString(vertexList, 'tmp_line');
    if (!this.polygon) {
      this.line.properties._showVertexLength = this.controller.interaction.hasVertexLength();
    }

    this.displayDrawFeature(this.line);


    let points = 64;

    const center = state.vertexList[0];

    if (!center) {
      return;
    }
    const geomArray = this.line.geometry.coordinates[1];
    if(!geomArray || !geomArray[0]){
      return;
    }

    let radiusInKm = this.getDistanceFromLatLonInKm(center[1], center[0], geomArray[1], geomArray[0]);

    this.text = this.createLabel([center[0], center[1]], `${new Number(radiusInKm).toFixed(2)}km`, {id: 'tmp_text'})

    this.displayDrawFeature(this.text);


    var coords = {
      latitude: center[1],
      longitude: center[0],
    };

    var km = radiusInKm;

    var ret = [];
    var distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    var distanceY = km / 110.574;

    var theta, x, y;
    for (var i = 0; i < points; i++) {
      theta = (i / points) * (2 * Math.PI);
      x = distanceX * Math.cos(theta);
      y = distanceY * Math.sin(theta);

      ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    this.polygon = this.createPolygon(ret, 'tmp_polygon');
    // console.log(this.polygon);
    this.displayDrawFeature(this.polygon);
  }
}
